export type Translation = { [lang: string]: string };

export type AssessmentQuestionType = 'weighted-mcq' | 'scale' | 'open-text';
export type LinkedQuestionType = 'weighted-mcq' | 'scale';

export interface WeightedMcqOption {
  text: Translation;
  points: number;
}

export interface ScaleConfig {
  min: number;
  max: number;
  step?: number;
  minLabel?: Translation;
  maxLabel?: Translation;
}

/** Question liée — partagée entre toutes les mesures. */
export interface LinkedQuestion {
  _id?: string;
  linkedKey?: string; // généré côté backend si absent
  type: LinkedQuestionType;
  question: Translation;
  order: number;
  options?: WeightedMcqOption[];
  scale?: ScaleConfig;
  /** True si au moins une réponse référence cette question (verrouillage de l'édition). */
  hasResponses?: boolean;
}

/** Question non liée — propre à une mesure. */
export interface AssessmentQuestion {
  _id?: string;
  type: AssessmentQuestionType;
  question: Translation;
  order: number;
  options?: WeightedMcqOption[];
  scale?: ScaleConfig;
}

export interface FollowupImpact {
  _id?: string;
  label: Translation;
  description?: Translation;
  questions: AssessmentQuestion[];
  order: number;
}

export interface CourseAssessment {
  id?: string;
  course?: string;
  linkedQuestions: LinkedQuestion[];
  initialDiagnosticDescription?: Translation;
  initialDiagnosticQuestions: AssessmentQuestion[];
  finalImpactDescription?: Translation;
  finalImpactQuestions: AssessmentQuestion[];
  followupImpacts: FollowupImpact[];
}

export interface CourseAssessmentResponseEnvelope {
  diagnosticImpactEnabled: boolean;
  assessment: CourseAssessment;
}

export const buildEmptyAssessment = (): CourseAssessment => ({
  linkedQuestions: [],
  initialDiagnosticQuestions: [],
  finalImpactQuestions: [],
  followupImpacts: []
});

export const buildNewLinkedQuestion = (
  order: number,
  type: LinkedQuestionType = 'scale'
): LinkedQuestion => ({
  type,
  question: {},
  order,
  options:
    type === 'weighted-mcq'
      ? [
          { text: {}, points: 0 },
          { text: {}, points: 0 }
        ]
      : undefined,
  scale: type === 'scale' ? { min: 1, max: 5 } : undefined
});

export const buildNewAssessmentQuestion = (
  order: number,
  type: AssessmentQuestionType = 'scale'
): AssessmentQuestion => ({
  type,
  question: {},
  order,
  options:
    type === 'weighted-mcq'
      ? [
          { text: {}, points: 0 },
          { text: {}, points: 0 }
        ]
      : undefined,
  scale: type === 'scale' ? { min: 1, max: 5 } : undefined
});

export const buildNewFollowupImpact = (order: number): FollowupImpact => ({
  label: {},
  description: {},
  questions: [],
  order
});

/**
 * Vérifie qu'au moins une langue contient une chaîne non vide.
 * (Un objet `{}` ou `{ fr: '' }` ne passe pas.)
 */
export const hasNonEmptyTranslation = (t?: Translation): boolean => {
  if (!t) return false;
  return Object.values(t).some((v) => typeof v === 'string' && v.trim().length > 0);
};

/**
 * Libellé affiché pour une traduction objet : langue préférée du cours, sinon première chaîne non vide.
 */
export const pickLocalizedTranslation = (
  t: Translation | undefined,
  options: { preferredLang: string; fallback: string }
): string => {
  if (!t || typeof t !== 'object') return options.fallback;
  const pref = t[options.preferredLang];
  if (typeof pref === 'string' && pref.trim()) return pref.trim();
  for (const v of Object.values(t)) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return options.fallback;
};

/**
 * Valide une CourseAssessment côté client.
 * Renvoie `null` si OK, sinon un message d'erreur localisé en français.
 */
export const validateAssessment = (a: CourseAssessment): string | null => {
  // Linked questions
  for (let i = 0; i < (a.linkedQuestions || []).length; i++) {
    const q = a.linkedQuestions[i];
    if (!hasNonEmptyTranslation(q.question)) {
      return `Question liée n°${i + 1} : le texte de la question est requis (au moins une langue).`;
    }
    if (q.type === 'weighted-mcq') {
      if (!q.options || q.options.length < 2) {
        return `Question liée n°${i + 1} : un QCM pondéré doit avoir au moins 2 options.`;
      }
      for (let j = 0; j < q.options.length; j++) {
        if (!hasNonEmptyTranslation(q.options[j].text)) {
          return `Question liée n°${i + 1} : option ${j + 1} — texte requis.`;
        }
      }
    }
    if (q.type === 'scale') {
      if (!q.scale || q.scale.min == null || q.scale.max == null) {
        return `Question liée n°${i + 1} : min et max sont requis pour une échelle.`;
      }
      if (q.scale.min >= q.scale.max) {
        return `Question liée n°${i + 1} : min doit être strictement inférieur à max.`;
      }
    }
  }

  const checkSpecific = (
    list: AssessmentQuestion[],
    sectionLabel: string
  ): string | null => {
    for (let i = 0; i < (list || []).length; i++) {
      const q = list[i];
      if (!hasNonEmptyTranslation(q.question)) {
        return `${sectionLabel} — question n°${i + 1} : le texte est requis.`;
      }
      if (q.type === 'weighted-mcq') {
        if (!q.options || q.options.length < 2) {
          return `${sectionLabel} — question n°${i + 1} : un QCM pondéré doit avoir au moins 2 options.`;
        }
        for (let j = 0; j < q.options.length; j++) {
          if (!hasNonEmptyTranslation(q.options[j].text)) {
            return `${sectionLabel} — question n°${i + 1} : option ${j + 1} — texte requis.`;
          }
        }
      }
      if (q.type === 'scale') {
        if (!q.scale || q.scale.min == null || q.scale.max == null) {
          return `${sectionLabel} — question n°${i + 1} : min et max sont requis pour une échelle.`;
        }
        if (q.scale.min >= q.scale.max) {
          return `${sectionLabel} — question n°${i + 1} : min doit être strictement inférieur à max.`;
        }
      }
    }
    return null;
  };

  let err = checkSpecific(a.initialDiagnosticQuestions, 'Diagnostic initial');
  if (err) return err;
  err = checkSpecific(a.finalImpactQuestions, 'Mesure d\'impact finale');
  if (err) return err;

  for (let i = 0; i < (a.followupImpacts || []).length; i++) {
    const f = a.followupImpacts[i];
    if (!hasNonEmptyTranslation(f.label)) {
      return `Mesure de suivi n°${i + 1} : le libellé est requis (au moins une langue).`;
    }
    err = checkSpecific(f.questions, `Mesure de suivi n°${i + 1}`);
    if (err) return err;
  }

  return null;
};
